/**
 * @file ErrorBoundary.tsx
 * @description Top-level React error boundary. Catches render errors AND
 *   lazy-chunk load failures (relevant since slice 14 split the bundle —
 *   a flaky CDN or a stale tab loading a chunk hash that's been replaced
 *   would otherwise white-screen the whole app). Renders a friendly
 *   recovery card with a "Reload" button. Stack trace is only shown in
 *   dev so prod users don't see internals.
 * @module Components/Common
 * @security The error's message + stack stay client-side; never sent
 *   anywhere by this component. If we later wire a /report endpoint,
 *   sanitize first — `error.message` can include data the user typed.
 */

import { Component, type ErrorInfo, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    // Structured console.error so the line shows up clearly in DevTools
    // and (when present) in the Cloudflare client-error tail. We don't
    // POST to /api/v1/client-error from here — the endpoint doesn't
    // exist yet and we don't want this component to depend on the
    // network at the moment the network may have caused the error.
    /* eslint-disable-next-line no-console */
    console.error('[ErrorBoundary] render crash', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  public render(): ReactNode {
    if (this.state.error === null) return this.props.children;
    return <ErrorCard error={this.state.error} />;
  }
}

function ErrorCard({ error }: { error: Error }): JSX.Element {
  const { t } = useTranslation();
  const isDev = import.meta.env.DEV === true;
  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white border border-red-200 rounded-lg shadow-sm p-6 space-y-4 text-center">
        <div className="text-4xl">⚠️</div>
        <h1 className="text-lg font-semibold">{t('error_boundary.title')}</h1>
        <p className="text-sm text-gray-600">{t('error_boundary.description')}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded bg-brand text-brand-fg px-4 py-2 text-sm font-medium hover:bg-brand-strong"
        >
          {t('error_boundary.reload')}
        </button>
        {isDev ? (
          <details className="text-left">
            <summary className="cursor-pointer text-xs text-gray-500">
              {t('error_boundary.dev_details')}
            </summary>
            <pre className="text-xs whitespace-pre-wrap break-words bg-gray-50 p-2 rounded mt-2">
              {error.message}
              {error.stack !== undefined ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        ) : null}
      </div>
    </main>
  );
}
